import React, {
  createContext, useContext, useEffect, useMemo, useRef,
} from 'react';
import { AirDropContext } from '../../utils/AirDropProvider';

interface RecaptchaProviderProps {
  siteKey: string,
  children: React.ReactNode
}

interface RecaptchaProviderValue {
  siteKey: string | undefined,
  executeScore: (action: string) => Promise<string>,
  isEnabled: () => boolean,
  getRecaptchaAction: (recaptchaActions: RecaptchaActionsType) => string
}

const RecaptchaContext = createContext<RecaptchaProviderValue>({
  siteKey: undefined,
  executeScore: () => Promise.reject(new Error('Recaptcha not initialized - did you wrap your component in RecaptchaProvider?')),
  isEnabled: () => false,
  getRecaptchaAction: () => 'NOT_IMPLEMENTED',
});

function RecaptchaProvider({ siteKey, children }: RecaptchaProviderProps) {
  const recaptchaBaseUrl = 'https://www.recaptcha.net/recaptcha/enterprise.js';
  const config = useContext(AirDropContext);

  // Since we do not need to render anything based on these values, we use refs rather than states.
  const isRecaptchaReady = useRef<boolean>(false);
  const recaptchaPromise = useRef<Promise<any> | null>(null);

  const isEnabled = () => config?.recaptchaOptions?.enabled;

  useEffect(() => {
    if (isEnabled()) {
      recaptchaPromise.current = new Promise((resolve, reject) => {
        if (window.grecaptcha?.enterprise) {
          resolve(window.grecaptcha);
        } else {
          const tag = document.createElement('script');

          tag.async = true;
          tag.defer = true;
          tag.src = `${recaptchaBaseUrl}?render=${siteKey}`;

          tag.addEventListener('load', () => {
            window.grecaptcha.enterprise.ready(() => {
              isRecaptchaReady.current = true;
              resolve(window.grecaptcha);
            });
          });

          tag.addEventListener('error', () => {
            reject(new Error('Failed to load Google reCAPTCHA library'));
          });

          document.body.appendChild(tag);
        }
      });
    }
  }, []);

  const getRecaptchaAction = (recaptchaActions: RecaptchaActionsType) => {
    const logoTreatment = config?.logoTreatment;

    let recaptchaActionKey: string = 'default';
    if (logoTreatment !== 'channels' && logoTreatment !== 'p_dot_com' && logoTreatment !== 'turbo') {
      recaptchaActionKey = 'legacy';
    }

    return recaptchaActions[recaptchaActionKey] ?? recaptchaActions.default;
  };

  const executeScore = (action: string) => {
    if (isRecaptchaReady.current) {
      return window.grecaptcha.enterprise.execute(siteKey, { action });
    }

    // For slow connections, it's possible that the user submits before recaptcha library has finished loading.
    // In these cases, we need to wait for recaptcha to finish loading before proceeding.
    return recaptchaPromise.current?.then((recaptcha: any) => recaptcha.enterprise.execute(siteKey, { action }));
  };

  const recaptchaFunctions = useMemo(() => (
    {
      siteKey,
      executeScore,
      isEnabled,
      getRecaptchaAction,
    }
  ), []);

  return <RecaptchaContext.Provider value={recaptchaFunctions}>{children}</RecaptchaContext.Provider>;
}

const appendTokenToUrl = (url: string, token: string | undefined) => {
  if (!token) {
    return url;
  }

  return `${url}${url.includes('?') ? '&' : '?'}g_recaptcha_response=${token}`;
};

export { RecaptchaProvider, RecaptchaContext, appendTokenToUrl };
